import { useIntl } from 'react-intl';
import { z } from 'zod';
import { IconBookmark, IconJson } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Avatar,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';

const valueSourceSchema = z.enum(['formula', 'memory']);

const structSchema = z
  .object({
    __query_index: z.number().optional(),
    __value_source: valueSourceSchema.optional(),
  })
  .catchall(z.unknown())
  .refine(obj => Object.keys(obj).length > 0, {
    message: 'Struct must have at least one key',
  });

type StructBadgeProps = {
  struct: unknown;
};

export const StructBadge = ({ struct }: StructBadgeProps) => {
  const intl = useIntl();
  const parsed = structSchema.safeParse(struct);

  if (!parsed.success) {
    return null;
  }

  const { data } = parsed;

  const queryIndex = data.__query_index ?? null;
  const valueSource = data.__value_source ?? null;

  const tableEntries = Object.entries(data).filter(
    ([key]) =>
      key !== '_id' &&
      key !== '__query_index' &&
      key !== '__value_source' &&
      key !== '__memory_key'
  );

  const getIcon = () => {
    if (queryIndex !== null) {
      return (
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: 'aurora.main',
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          {queryIndex + 1}
        </Typography>
      );
    }

    if (valueSource === 'memory') {
      return (
        <SvgIcon fontSize="small" sx={{ color: 'aurora.main' }}>
          <IconBookmark />
        </SvgIcon>
      );
    }

    if (tableEntries.length > 0) {
      return (
        <SvgIcon fontSize="small" sx={{ color: 'aurora.main' }}>
          <IconJson />
        </SvgIcon>
      );
    }

    return null;
  };

  const icon = getIcon();

  return icon ? (
    <Tooltip
      title={
        tableEntries.length > 0 ? (
          <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight="bold">
              {intl.formatMessage({
                id: 'features.queueSettings.fields.formulaPreview.table.additionalValues',
              })}
            </Typography>
            {tableEntries.map(([key, val]) => (
              <Stack
                key={key}
                direction="row"
                spacing={1}
                justifyContent="space-between"
              >
                <Typography variant="caption" color="text.secondary">
                  {key}
                </Typography>
                <Typography variant="caption">
                  {typeof val === 'string' ? val : JSON.stringify(val)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          ''
        )
      }
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          backgroundColor: t => alpha(t.palette.aurora.main, 0.1),
        }}
      >
        {icon}
      </Avatar>
    </Tooltip>
  ) : null;
};
